import React, { useState } from 'react';
import { Box, TextField, Button, Alert, InputAdornment, Typography } from '@mui/material';
import { Email, ArrowBack, Lock, Visibility, VisibilityOff } from '@mui/icons-material';
import { LoginFormProps } from '../../shared/types';
import { useThemeMode } from '../../../../contexts/ThemeProvider';

const ForgotPasswordForm = ({ authLogic, config }: LoginFormProps) => {
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';

  const { formData, errors, isLoading, updateFormData, backToLogin, handleVerifyAccount, handlePasswordResetConfirm } = authLogic;
  const primaryColor = config?.theme?.primary || '#002465';

  const [isVerified, setIsVerified] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [validationErrors, setValidationErrors] = useState<any>({});
  const [resetSuccess, setResetSuccess] = useState(false);

  const handleVerifyAccountSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSuccessMessage('');

    const result = await handleVerifyAccount(event);

    if (result.success) {
      setSuccessMessage(result.message || '');
      setIsVerified(true);
      
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    }
  };

  const handleConfirmPasswordSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setValidationErrors({});

    const result = await handlePasswordResetConfirm(verificationCode, newPassword, confirmPassword);

    if (result.success) {
      setResetSuccess(true);
    } else if (result.errors) {
      setValidationErrors(result.errors);
    }
  };

  const textFieldStyles = {
    input: { color: isDark ? '#ffffff' : 'black' },
    mb: 2.5,
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'transparent',
      minHeight: '48px',
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: isDark ? '#ffffff' : '#d1d5db'
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: isDark ? '#ffffff' : primaryColor,
        borderWidth: 2
      }
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: isDark ? 'rgba(255, 255, 255, 0.3)' : '#e5e7eb'
    },
    '& .MuiInputLabel-root': {
      color: isDark ? 'rgba(255,255,255,0.7)' : '#6b7280',
      '&.Mui-focused': {
        color: isDark ? '#ffffff' : primaryColor
      }
    },
    '& .MuiFormHelperText-root': {
      color: '#f87171',
      fontSize: '0.75rem'
    }
  };

  if (resetSuccess) {
    return (
      <Box sx={{ textAlign: 'center' }}>
        <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
          Password reset successfully! You can now sign in with your new password.
        </Alert>

        <Button
          onClick={backToLogin}
          variant="contained"
          size="large"
          sx={{
            py: 2,
            px: 4,
            fontSize: '1rem',
            fontWeight: 600,
            borderRadius: 2,
            textTransform: 'none',
            backgroundColor: isDark ? '#ffffff' : primaryColor,
            color: isDark ? '#000000' : '#ffffff',
            '&:hover': {
              backgroundColor: isDark ? 'rgba(255, 255, 255, 0.9)' : primaryColor,
              filter: 'brightness(0.9)'
            }
          }}
        >
          Back to Sign In
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box textAlign="center" mb={3}>
        <Typography variant="h4" fontWeight="700" color={isDark ? '#ffffff' : '#1f2937'} mb={2}>
          {isVerified ? 'Set New Password' : 'Reset Password'}
        </Typography>
        <Typography variant="body1" color={isDark ? 'rgba(255,255,255,0.7)' : '#6b7280'}>
          {isVerified ? 'Create a password that is different from your login password' : 'Enter your email to receive reset instructions'}
        </Typography>
      </Box>

      {successMessage && (
        <Alert
          severity="success"
          sx={{
            mb: 3,
            borderRadius: 2,
            backgroundColor: isDark ? 'rgba(76, 175, 80, 0.15)' : undefined,
            color: isDark ? '#ffffff' : undefined,
            border: isDark ? '1px solid rgba(76, 175, 80, 0.3)' : undefined,
            '& .MuiAlert-icon': {
              color: isDark ? '#4caf50' : undefined
            }
          }}
        >
          {successMessage}
        </Alert>
      )}

      {errors.general && (
        <Alert
          severity="error"
          sx={{
            mb: 3,
            borderRadius: 2,
            backgroundColor: isDark ? 'rgba(244, 67, 54, 0.15)' : undefined,
            color: isDark ? '#ffffff' : undefined,
            border: isDark ? '1px solid rgba(244, 67, 54, 0.3)' : undefined,
            '& .MuiAlert-icon': {
              color: isDark ? '#f44336' : undefined
            }
          }}
        >
          {errors.general}
        </Alert>
      )}

      <TextField
        fullWidth
        label="Email address"
        type="email"
        name="email"
        autoComplete="email"
        placeholder="Enter your email"
        value={formData.resetEmail}
        onChange={e => updateFormData('resetEmail', e.target.value)}
        error={!!errors.resetEmail}
        helperText={errors.resetEmail}
        disabled={isLoading || isVerified}
        sx={textFieldStyles}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Email sx={{ color: isDark ? 'rgba(255,255,255,0.7)' : '#9ca3af', fontSize: 20 }} />
            </InputAdornment>
          )
        }}
      />

      {!isVerified && (
        <Button
          onClick={handleVerifyAccountSubmit}
          fullWidth
          variant="contained"
          size="large"
          disabled={isLoading}
          sx={{
            py: 2,
            fontSize: '1rem',
            fontWeight: 600,
            borderRadius: 2,
            textTransform: 'none',
            backgroundColor: isDark ? '#ffffff' : primaryColor,
            color: isDark ? '#000000' : '#ffffff',
            boxShadow: isDark ? '0 4px 12px rgba(255, 255, 255, 0.2)' : `0 4px 12px rgba(0, 36, 101, 0.2)`,
            cursor: isLoading ? 'not-allowed' : 'pointer',
            opacity: isLoading ? 0.7 : 1,
            mb: 3,
            '&:hover': {
              backgroundColor: isDark ? (isLoading ? '#ffffff' : 'rgba(255, 255, 255, 0.9)') : isLoading ? primaryColor : primaryColor,
              filter: isLoading ? 'none' : 'brightness(0.9)'
            },
            '&:disabled': {
              backgroundColor: isDark ? '#ffffff' : primaryColor,
              color: isDark ? '#000000' : 'white'
            }
          }}
        >
          {isLoading ? (
            <Box display="flex" alignItems="center" gap={1}>
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  border: isDark ? '2px solid rgba(0,0,0,0.3)' : '2px solid rgba(255,255,255,0.3)',
                  borderTop: isDark ? '2px solid black' : '2px solid white',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  '@keyframes spin': {
                    '0%': { transform: 'rotate(0deg)' },
                    '100%': { transform: 'rotate(360deg)' }
                  }
                }}
              />
              Verifying...
            </Box>
          ) : (
            'Verify Account'
          )}
        </Button>
      )}

      {isVerified && (
        <Box>
          <TextField
            fullWidth
            label="Verification code"
            name="verificationCode"
            placeholder="Enter verification code"
            value={verificationCode}
            onChange={e => setVerificationCode(e.target.value)}
            error={!!validationErrors.verificationCode}
            helperText={validationErrors.verificationCode}
            disabled={isLoading}
            sx={{ ...textFieldStyles, mb: 2.5 }}
          />

          <TextField
            fullWidth
            label="New password"
            type={showNewPassword ? 'text' : 'password'}
            name="newPassword"
            placeholder="••••••••••"
            value={newPassword}
            onChange={e => setNewPassword(e.target.value)}
            error={!!validationErrors.password}
            helperText={validationErrors.password}
            disabled={isLoading}
            sx={{ ...textFieldStyles, mb: 2.5 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock sx={{ color: isDark ? 'rgba(255,255,255,0.7)' : '#9ca3af', fontSize: 20 }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <Button
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    type="button"
                    sx={{
                      color: isDark ? 'rgba(255,255,255,0.7)' : '#9ca3af',
                      minWidth: 'auto',
                      p: 1
                    }}
                  >
                    {showNewPassword ? <VisibilityOff /> : <Visibility />}
                  </Button>
                </InputAdornment>
              )
            }}
          />

          <TextField
            fullWidth
            label="Confirm Password"
            type={showConfirmPassword ? 'text' : 'password'}
            name="confirmPassword"
            placeholder="••••••••••"
            value={confirmPassword}
            onChange={e => setConfirmPassword(e.target.value)}
            error={!!validationErrors.confirmPassword}
            helperText={validationErrors.confirmPassword}
            disabled={isLoading}
            sx={{ ...textFieldStyles, mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock sx={{ color: isDark ? 'rgba(255,255,255,0.7)' : '#9ca3af', fontSize: 20 }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <Button
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    type="button"
                    sx={{
                      color: isDark ? 'rgba(255,255,255,0.7)' : '#9ca3af',
                      minWidth: 'auto',
                      p: 1
                    }}
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </Button>
                </InputAdornment>
              )
            }}
          />

          <Box sx={{ mb: 2, bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)', p: 2, borderRadius: 1 }}>
            <Typography variant="body2" sx={{ color: isDark ? 'rgba(255,255,255,0.7)' : '#6b7280', mb: 1, fontWeight: 500, fontSize: '0.8rem' }}>
              Password requirements:
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 0.5,
                fontSize: '0.75rem',
                color: isDark ? 'rgba(255,255,255,0.6)' : '#6b7280'
              }}
            >
              <Box>• 8+ characters</Box>
              <Box>• 1+ number</Box>
              <Box>• 1+ lowercase</Box>
              <Box>• 1+ uppercase</Box>
              <Box sx={{ gridColumn: { xs: '1', sm: '1 / -1' } }}>• 1+ special character (! @ # $ % ^ & *)</Box>
            </Box>
          </Box>

          <Button
            onClick={handleConfirmPasswordSubmit}
            fullWidth
            variant="contained"
            size="large"
            disabled={isLoading}
            sx={{
              py: 1.5,
              fontSize: '1rem',
              fontWeight: 600,
              borderRadius: 2,
              textTransform: 'none',
              backgroundColor: isDark ? '#ffffff' : primaryColor,
              color: isDark ? '#000000' : '#ffffff',
              boxShadow: isDark ? '0 4px 12px rgba(255, 255, 255, 0.2)' : `0 4px 12px rgba(0, 36, 101, 0.2)`,
              cursor: isLoading ? 'not-allowed' : 'pointer',
              opacity: isLoading ? 0.7 : 1,
              '&:hover': {
                backgroundColor: isDark ? (isLoading ? '#ffffff' : 'rgba(255, 255, 255, 0.9)') : isLoading ? primaryColor : primaryColor,
                filter: isLoading ? 'none' : 'brightness(0.9)'
              },
              '&:disabled': {
                backgroundColor: isDark ? '#ffffff' : primaryColor,
                color: isDark ? '#000000' : 'white'
              }
            }}
          >
            {isLoading ? (
              <Box display="flex" alignItems="center" gap={1}>
                <Box
                  sx={{
                    width: 20,
                    height: 20,
                    border: isDark ? '2px solid rgba(0,0,0,0.3)' : '2px solid rgba(255,255,255,0.3)',
                    borderTop: isDark ? '2px solid black' : '2px solid white',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    '@keyframes spin': {
                      '0%': { transform: 'rotate(0deg)' },
                      '100%': { transform: 'rotate(360deg)' }
                    }
                  }}
                />
                Resetting Password...
              </Box>
            ) : (
              'RESET PASSWORD'
            )}
          </Button>
        </Box>
      )}

      <Button
        startIcon={<ArrowBack sx={{ color: isDark ? 'rgba(255,255,255,0.7)' : '#6b7280' }} />}
        onClick={backToLogin}
        type="button"
        disabled={isLoading}
        sx={{
          mt: 2,
          textTransform: 'none',
          color: isDark ? '#ffffff' : '#6b7280',
          fontWeight: 500,
          cursor: isLoading ? 'not-allowed' : 'pointer',
          opacity: isLoading ? 0.6 : 1,
          minHeight: { xs: '44px', md: '40px' },
          '&:hover': {
            color: isDark ? 'rgba(255,255,255,0.8)' : primaryColor,
            backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : '#f9fafb'
          }
        }}
      >
        Back to sign in
      </Button>
    </Box>
  );
};

export default ForgotPasswordForm;